import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Clock, Gift, ArrowRight, X } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Session-scoped hook — dismissed banner stays hidden for the tab session only. */
const useSessionDismiss = (code) => {
  const key = code ? `personal_promo_dismissed_${code}` : null;
  const [dismissed, setDismissed] = useState(() => {
    if (!key) return false;
    try { return sessionStorage.getItem(key) === '1'; } catch { return false; }
  });
  const dismiss = () => {
    if (!key) return;
    try { sessionStorage.setItem(key, '1'); } catch {}
    setDismissed(true);
  };
  return [dismissed, dismiss];
};

const useCountdown = (endsAt) => {
  const compute = () => {
    if (!endsAt) return null;
    const ms = new Date(endsAt).getTime() - Date.now();
    if (isNaN(ms) || ms <= 0) return null;
    const h = Math.floor(ms / 3600000);
    const m = Math.floor((ms % 3600000) / 60000);
    const s = Math.floor((ms % 60000) / 1000);
    return { h, m, s, ms };
  };
  const [tr, setTr] = useState(compute);
  useEffect(() => {
    if (!endsAt) { setTr(null); return; }
    setTr(compute());
    const id = setInterval(() => setTr(compute()), 1000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endsAt]);
  return tr;
};

/**
 * Personal welcome-code banner. Only renders for logged-in users who have an
 * active un-redeemed WELCOME code. Ticks the 48h countdown every second.
 * Dismissal is session-scoped so the nudge returns on the next browser session.
 */
const PersonalPromoBanner = ({ variant = 'default' }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [promo, setPromo] = useState(null);
  const [dismissed, dismiss] = useSessionDismiss(promo?.code);
  const tr = useCountdown(promo?.ends_at);

  useEffect(() => {
    if (!user) { setPromo(null); return; }
    let alive = true;
    axios.get(`${API}/promotions/my-active`)
      .then(r => { if (alive && r.data?.code) setPromo(r.data); })
      .catch(() => {});
    return () => { alive = false; };
  }, [user]);

  if (!user || !promo || !promo.code || dismissed || !tr) return null;

  const label = tr.h > 0
    ? `${tr.h}h ${String(tr.m).padStart(2, '0')}m ${String(tr.s).padStart(2, '0')}s`
    : `${tr.m}m ${String(tr.s).padStart(2, '0')}s`;

  const redeem = () => navigate(`/pricing?promo=${encodeURIComponent(promo.code)}&utm_source=personal_banner`);

  return (
    <div
      className="relative overflow-hidden rounded-xl border-2 border-[#D4A017] bg-gradient-to-br from-amber-50 via-yellow-50 to-white p-3 sm:p-4 mb-4"
      data-testid="personal-promo-banner"
      role="region"
      aria-label="Your personal welcome offer"
    >
      <div className="absolute -top-6 -right-6 w-24 h-24 bg-yellow-300/25 rounded-full pointer-events-none" />
      <div className="relative flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div className="shrink-0 w-10 h-10 rounded-lg bg-[#D4A017] flex items-center justify-center text-[#0F2C3E]">
            <Gift className="h-5 w-5" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm sm:text-base font-bold text-[#0F2C3E] leading-tight">
              Your welcome gift: <span className="text-[#B45309]">50% off — 3 months (monthly) or 6 months equivalent, £36 off (annual)</span>
            </p>
            <p className="text-xs text-slate-600 mt-1 flex items-center gap-1.5 flex-wrap">
              <span className="font-mono font-bold bg-slate-900 text-yellow-300 px-1.5 py-0.5 rounded text-[11px]" data-testid="personal-promo-code">{promo.code}</span>
              <span className="inline-flex items-center gap-1 text-red-700 font-semibold">
                <Clock className="h-3 w-3" /> Ends in <span data-testid="personal-promo-countdown" aria-live="polite">{label}</span>
              </span>
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0 self-stretch sm:self-auto w-full sm:w-auto">
          <button
            onClick={redeem}
            className="flex-1 sm:flex-none inline-flex items-center justify-center gap-1.5 bg-[#D4A017] hover:bg-[#c39115] text-[#0F2C3E] font-bold text-sm px-4 py-2 rounded-lg transition-colors"
            data-testid="personal-promo-redeem-btn"
          >
            Redeem now <ArrowRight className="h-3.5 w-3.5" />
          </button>
          <button
            onClick={dismiss}
            aria-label="Dismiss offer for this session"
            className="p-1.5 rounded-md hover:bg-slate-100 text-slate-500"
            data-testid="personal-promo-dismiss"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default PersonalPromoBanner;
